import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/use-auth'
import { Layout } from '@/components/layout'
import { LandingPage } from '@/pages/landing'
import { FeedPage } from '@/pages/feed'
import { VideoPage } from '@/pages/video'
import { ChannelsPage } from '@/pages/channels'
import { LibraryPage } from '@/pages/library'
import { BriefingsPage } from '@/pages/briefings'
import { OnboardingPage } from '@/pages/onboarding'
import { SettingsPage } from '@/pages/settings'
import { UpgradePage } from '@/pages/upgrade'
import { ChangelogPage } from '@/pages/changelog'
import { Skeleton } from '@/components/ui/skeleton'
import { initPaddle } from '@/lib/paddle'
import { api } from '@/lib/api'
import { captureEvent, consumeOauthCallbackIfPending } from '@/lib/analytics'
import { useActivityHeartbeat } from '@/hooks/use-activity-heartbeat'

export default function App() {
  const { user, loading, logout } = useAuth()

  useEffect(() => { initPaddle() }, [])

  // Heartbeat while the app is visible and the user is active — drives the
  // "minutes active per day" signal.
  useActivityHeartbeat(Boolean(user))

  // Capture ?plan= param from marketing page before OAuth redirects away
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const plan = params.get('plan')
    if (plan && ['plus', 'max'].includes(plan)) {
      sessionStorage.setItem('pendingPlan', plan)
    }
    // ?claim_v=<youtube id> from a public /v/ signup CTA — stash it across the
    // OAuth round trip so we can favorite + open that video once signed in.
    const claimV = params.get('claim_v')
    if (claimV) sessionStorage.setItem('pendingClaimVideo', claimV)
  }, [])

  // OAuth funnel close-out: if the redirect flag was set just before the Google
  // handoff, this load is the post-OAuth return — emit success with round-trip latency.
  useEffect(() => {
    if (!user || loading) return
    const { wasFreshAuth, latencyMs } = consumeOauthCallbackIfPending()
    if (wasFreshAuth) {
      captureEvent('oauth_callback_success', {
        provider: 'google',
        round_trip_ms: latencyMs,
        plan: user.billing_plan,
      })
    }
  }, [user, loading])

  // After login, redirect to /upgrade if there's a pending plan
  useEffect(() => {
    if (user && !loading) {
      const pendingPlan = sessionStorage.getItem('pendingPlan')
      if (pendingPlan) {
        sessionStorage.removeItem('pendingPlan')
        window.location.href = '/upgrade'
      }
    }
  }, [user, loading])

  // After login, claim a video the user summarized while logged out: favorite
  // it (unlocks access) and open it, so a brand-new account with no follows
  // still lands on the thing they came for. Best-effort — a missing video just
  // leaves them on the feed.
  useEffect(() => {
    if (!user || loading) return
    const claimYt = sessionStorage.getItem('pendingClaimVideo')
    if (!claimYt) return
    sessionStorage.removeItem('pendingClaimVideo')
    captureEvent('rundown_claim_attempted', { youtube_video_id: claimYt })
    api.claimVideo(claimYt)
      .then(({ videoId }) => {
        captureEvent('rundown_claimed', { youtube_video_id: claimYt })
        window.location.href = `/videos/${videoId}`
      })
      .catch(() => { /* not found / not ready — stay on the feed */ })
  }, [user, loading])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="space-y-4 text-center">
          <Skeleton className="mx-auto h-10 w-10 rounded-lg" />
          <Skeleton className="mx-auto h-4 w-32" />
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <Routes>
        <Route path="*" element={<LandingPage />} />
      </Routes>
    )
  }

  return (
    <Layout user={user} onLogout={logout}>
      <Routes>
        <Route path="/feed" element={<FeedPage />} />
        <Route path="/briefings" element={<BriefingsPage />} />
        <Route path="/library" element={<LibraryPage />} />
        <Route path="/videos/:videoId" element={<VideoPage />} />
        <Route path="/channels" element={<ChannelsPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/upgrade" element={<UpgradePage />} />
        <Route path="/changelog" element={<ChangelogPage />} />
        <Route path="*" element={<Navigate to="/feed" replace />} />
      </Routes>
    </Layout>
  )
}
